import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Listen for variant updates - primarily on document to ensure we catch all events
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this));

    // Also try to listen on section if available
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove document listener
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this));

    // Also remove section listener if it exists
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this));
    }
  }
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const input = this.refs.quantityInput;

    // ✅ Ensure the value is numeric before stepping up
    if (isNaN(parseFloat(input.value))) {
      input.value = (input.min || 1).toString();
    }

    input.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const input = this.refs.quantityInput;

    // ✅ Ensure the value is numeric before stepping down
    if (isNaN(parseFloat(input.value))) {
      input.value = (input.min || 1).toString();
    }

    input.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value, 10);

    // Only dispatch cart update events if we're on a cart page (has cart line)
    const cartLine = quantityInput.dataset.cartLine ? Number(quantityInput.dataset.cartLine) : null;
    if (cartLine) {
      quantityInput.dispatchEvent(
        new QuantitySelectorUpdateEvent(newValue, cartLine)
      );
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min, 10) || 1;
    const max = parseInt(quantityInput.max, 10) || Infinity;
    let newValue = parseInt(quantityInput.value, 10) || min;

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    quantityInput.value = newValue.toString();
  };

  /**
   * Handles variant update events to update the quantity selector with new variant data
   * @param {VariantUpdateEvent} event - The variant update event
   */
  #onVariantUpdate(event) {
    // First, if we have the variant's quantity_rule directly on the event,
    // use that to update the min / max / step for this quantity input.
    const variant = event.detail?.resource;
    if (variant && typeof variant === 'object' && variant.quantity_rule) {
      const { quantityInput } = this.refs;
      const rule = variant.quantity_rule;

      if (rule.min != null) {
        const minValue = parseInt(rule.min, 10) || 1;
        quantityInput.min = String(minValue);
        quantityInput.value = String(minValue);
      }

      if (rule.max != null) {
        quantityInput.max = String(parseInt(rule.max, 10));
      } else {
        quantityInput.removeAttribute('max');
      }

      if (rule.increment != null) {
        quantityInput.step = String(parseInt(rule.increment, 10) || 1);
      } else {
        quantityInput.step = '1';
      }

      this.#checkQuantityRules();
      return;
    }

    // Check if this is for a different product
    if (event.detail.data.newProduct) {
      // Product changed, update product ID
      const productForm = this.closest('product-form-component');
      if (productForm instanceof HTMLElement) {
        productForm.dataset.productId = event.detail.data.newProduct.id;
      }
    } else {
      const productElement = this.closest('[data-product-id]');
      if (event.target instanceof HTMLElement && productElement instanceof HTMLElement &&
          event.target.dataset.productId !== productElement.dataset.productId) {
        return;
      }
    }

    // Fallback: find the updated quantity selector in the new HTML
    const newQuantitySelector = event.detail.data.html.querySelector('quantity-selector-component');
    if (!newQuantitySelector) return;

    // Get the new input element from the updated HTML
    const newInput = newQuantitySelector.querySelector('input[type="number"]');
    if (!(newInput instanceof HTMLInputElement)) return;

    // Update only the input attributes instead of replacing all HTML
    const currentInput = this.querySelector('input[type="number"]');
    if (!(currentInput instanceof HTMLInputElement)) return;

    // Update the attributes that change with variant
    const newMin = newInput.getAttribute('min');
    const newMax = newInput.getAttribute('max');
    const newStep = newInput.getAttribute('step');

    if (newMin !== null) currentInput.setAttribute('min', newMin);
    if (newMax !== null) currentInput.setAttribute('max', newMax);
    if (newStep !== null) currentInput.setAttribute('step', newStep);

    // Always reset to minimum quantity when variant changes
    if (newMin !== null) {
      const minValue = parseInt(newMin, 10);
      currentInput.value = minValue.toString();

      // Trigger validation to ensure it meets rules
      this.#checkQuantityRules();
    }
  }


  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}